import { createSlice } from '@reduxjs/toolkit';

/**
 * Redux slice for managing shared loading states across the application
 *
 * Uses an object to track active loading states by key.
 * Only stores active loading states (memory efficient).
 */
const initialState = {
    // Object of active loading states: { 'key1': true, 'key2': true }
    activeLoaders: {},
};

export const loadingStatesSlice = createSlice({
    name: 'loadingStates',
    initialState,
    reducers: {
        /**
         * Start loading for a specific key
         * @param {Object} state - Current state
         * @param {Object} action - Action with payload: { key: string }
         */
        startLoading: (state, action) => {
            const key = action.payload;
            // Only update if not already loading (prevents unnecessary re-renders)
            if (!state.activeLoaders[key]) {
                state.activeLoaders[key] = true;
            }
        },
        /**
         * Stop loading for a specific key
         * @param {Object} state - Current state
         * @param {Object} action - Action with payload: { key: string }
         */
        stopLoading: (state, action) => {
            const key = action.payload;
            // Only update if currently loading (prevents unnecessary re-renders)
            if (state.activeLoaders[key]) {
                delete state.activeLoaders[key];
            }
        },
        /**
         * Reset all loading states
         */
        resetAllLoading: (state) => {
            state.activeLoaders = {};
        },
        /**
         * Reset a group of loading states at once
         * @param {Object} state - Current state
         * @param {Object} action - Action with payload: string[] of keys
         */
        resetLoadingKeys: (state, action) => {
            const keys = action.payload || [];
            keys.forEach((key) => {
                if (state.activeLoaders[key]) {
                    delete state.activeLoaders[key];
                }
            });
        },
        /**
         * Reset specific loading state
         * @param {Object} state - Current state
         * @param {Object} action - Action with payload: { key: string }
         */
        resetLoading: (state, action) => {
            const key = action.payload;
            if (state.activeLoaders[key]) {
                delete state.activeLoaders[key];
            }
        },
    },
});

export const { startLoading, stopLoading, resetAllLoading, resetLoading, resetLoadingKeys } =
    loadingStatesSlice.actions;

export default loadingStatesSlice.reducer;
